import cn from "classnames";
import * as React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { resolveInject } from "app/di";
import { HierarchyConfig } from "app/services/config/app-config";
import {
    TreeDataLoaderService,
    TreeRecord
} from "app/services/data-loader/tree-data-loader.service";
import { SparqlRecord, SparqlResultParser } from "app/services/infrastructure/sparql-result-parser";
import { ITerm, Term, TermBuilder } from "app/services/data-model";
import { ISparqlExecutor } from "app/services/infrastructure/sparql-executor";
import { ErrorMessage } from "app/ui/_generic/error-message";
import { TextItem } from "app/ui/elements/text-item";

import "./hierarchy.less";

export interface IHierarchyState {
    data: TreeRecord[];
    isLoading?: boolean;
    error?: Error;
    activeLetterFilter?: string;
    dataUriList: string[];
}

export interface IHierarchyProps {
    config: HierarchyConfig;
    currentTerm?: ITerm;
    onSelect(node: ITerm, parents: ITerm[]): void;
}

export class Hierarchy extends React.Component<IHierarchyProps, IHierarchyState> {
    private sparqlExecutor = resolveInject(ISparqlExecutor);
    private loader: TreeDataLoaderService;

    constructor(props: IHierarchyProps) {
        super(props);
        this.state = {
            data: [],
            dataUriList: [],
            isLoading: true
        };
        this.loader = new TreeDataLoaderService(
            this.sparqlExecutor,
            this.props.config,
            this.props.currentTerm?.iri
        );
    }

    async componentDidMount() {
        await this.loadData(this.props, true);
    }

    async componentDidUpdate(prevProps: IHierarchyProps, prevState: IHierarchyState) {
        const activeFilterLetterChanged =
            prevState.activeLetterFilter !== this.state.activeLetterFilter;
        if (
            prevProps.config.id != this.props.config.id ||
            prevProps.currentTerm != this.props.currentTerm ||
            activeFilterLetterChanged
        ) {
            this.loader = new TreeDataLoaderService(
                this.sparqlExecutor,
                this.props.config,
                this.props.currentTerm?.iri
            );
            const shouldReloadData =
                prevProps.config.id != this.props.config.id || activeFilterLetterChanged;
            await this.loadData(this.props, shouldReloadData);

            this.scrollElementIntoView();
        }
    }

    scrollElementIntoView() {
        try {
            this.getElementsByText(this.props.currentTerm?.name || "", "a")[0].scrollIntoView({
                behavior: "auto",
                block: "center",
                inline: "start"
            });
        } catch (err) {
            return;
        }
    }

    getElementsByText(str: string, tag = "a") {
        return Array.prototype.slice
            .call(document.getElementsByTagName(tag))
            .filter((el: any) => el.textContent.trim() === str.trim());
    }

    filterRecords = (data: TreeRecord[], filter?: string) => {
        if (!filter) {
            return data;
        }

        if (filter === "0-9") {
            // return [...data.filter(x => x.record.entry_text?.value.toLowerCase().startsWith(/[0-9]/))]
            return [...data.filter((x) => /^\d/.test(x.record.entry_text?.value))];
        }

        return (data = [
            ...data.filter((x) =>
                x.record.entry_text?.value.toLowerCase().startsWith(filter.toLocaleLowerCase())
            )
        ]);
    };

    // Recursively create the hierarchy of node starting from the current term and going up
    calculateHierarchyWithoutRoot = (
        records: SparqlRecord[],
        iri: string,
        hierarchyPath: string[]
    ) => {
        const parentRecord = records.find((record) => record.grandchild?.value == iri);
        if (parentRecord) {
            const parentIri = parentRecord.entry_iri?.value;
            hierarchyPath.unshift(parentIri);
            this.calculateHierarchyWithoutRoot(records, parentIri, hierarchyPath);
        }
    };

    getRetrievedIris = (node: TreeRecord, list: string[]) => {
        list.push(node.uri);
        node.children?.forEach((child) => this.getRetrievedIris(child, list));
    };

    addRootToHierarchy = async (
        currentTermIri: string,
        rootQuery: string,
        hierarchyPath: string[]
    ) => {
        let firstChild = currentTermIri;
        if (hierarchyPath.length > 0) {
            firstChild = hierarchyPath[0];
        }

        const rootResult = await this.sparqlExecutor.execute(rootQuery, [
            { name: "rootchild", type: "uri", value: firstChild }
        ]);
        const rootRecords = SparqlResultParser.parse(rootResult);

        if (rootRecords.length == 0) {
            return;
        }

        const root = rootRecords[0].entry_iri?.value;
        hierarchyPath.unshift(root);
    };

    /**
     * If the required queries exist, calculate the hierarchy fom the current term
     * @returns A list of node ids as the path.
     */
    retrieveExpandedPath = async (currentTermIri: string, config: HierarchyConfig) => {
        const childQuery = this.props.config.dynamicTree?.children;
        const rootQuery = this.props.config.dynamicTree?.roots;
        const hierarchyPath: string[] = [];

        if (!childQuery || !rootQuery) {
            return hierarchyPath;
        }

        const result = await this.sparqlExecutor.execute(childQuery, []);
        const records = SparqlResultParser.parse(result);

        this.calculateHierarchyWithoutRoot(records, currentTermIri, hierarchyPath);
        await this.addRootToHierarchy(currentTermIri, rootQuery, hierarchyPath);

        return hierarchyPath;
    };

    async loadData(props: IHierarchyProps, withReload: boolean): Promise<void> {
        const { activeLetterFilter, data, dataUriList } = this.state;
        const { currentTerm, config } = this.props;

        try {
            let hierarchyPath = currentTerm?.iri
                ? await this.retrieveExpandedPath(currentTerm.iri, config)
                : [];
            let newData = this.filterRecords(
                await this.loader.loadRootTreeLevelWithSelectedNode(
                    withReload ? [] : data,
                    hierarchyPath
                ),
                activeLetterFilter
            );

            // Keep track of retrieved node iris
            const flatList: string[] = [];
            newData.forEach((n) => this.getRetrievedIris(n, flatList));

            this.setState({ data: newData, isLoading: false, dataUriList });
        } catch (ex) {
            this.setState({ error: ex as Error, isLoading: false });
        }
    }

    renderFilter = () => {
        const { activeLetterFilter } = this.state;
        return (
            <ul
                style={{
                    flexWrap: "wrap",
                    display: "flex",
                    borderBottom: "2px solid ",
                    padding: 0
                }}
            >
                <li
                    key="all"
                    onClick={() => this.setState({ ...this.state, activeLetterFilter: undefined })}
                    className={`letter-filter ${!activeLetterFilter && "selected"}`}
                >
                    All
                </li>
                {Array.from(Array(26), (_, index) => String.fromCharCode(97 + index)).map(
                    (letter) => (
                        <li
                            key={letter}
                            onClick={() =>
                                this.setState({ ...this.state, activeLetterFilter: letter })
                            }
                            className={`letter-filter ${
                                activeLetterFilter === letter && "selected"
                            }`}
                        >
                            <a>{letter.toUpperCase()}</a>
                        </li>
                    )
                )}
                <li
                    key="0-9"
                    onClick={() => this.setState({ ...this.state, activeLetterFilter: "0-9" })}
                    className={`letter-filter ${activeLetterFilter === "0-9" && "selected"}`}
                >
                    0-9
                </li>
            </ul>
        );
    };

    render() {
        if (this.state.isLoading) {
            return (
                <section
                    className="hierarchy"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%"
                    }}
                >
                    <CircularProgress color="primary" />
                </section>
            );
        } else {
            return (
                <section className="hierarchy" style={{ whiteSpace: "nowrap", overflow: "auto" }}>
                    {this.props.config.name === "Alphabetical" && this.renderFilter()}
                    {!this.state.error ? (
                        this.renderTree()
                    ) : (
                        <ErrorMessage error={this.state.error} />
                    )}
                </section>
            );
        }
    }

    renderTree() {
        const config = this.props.config;
        return (
            <table className={cn("table")} id={config.id} style={{ border: "none" }}>
                <tbody style={{ borderBottom: "none" }}>
                    {this.renderTreeRecords(this.state.data, [])}
                </tbody>
            </table>
        );
    }

    renderTreeRecords(records: TreeRecord[], parents: ITerm[]): React.JSX.Element[] {
        return records.map((record, irow) => {
            const term = TermBuilder.parseFromSparqlRecord(record.record);
            return (
                <React.Fragment key={"row_" + irow}>
                    <tr>
                        <td key={"cell_" + irow + "_" + 0}>
                            {this.renderStubsAndExpander(record)}
                            <TextItem
                                text={term.name || Term.getLocalname(term.iri)}
                                link={term.iri}
                                icon={term.icon}
                                highlighted={
                                    this.props.currentTerm && term.iri == this.props.currentTerm.iri
                                }
                                onClick={() => {
                                    this.props.onSelect(term, parents);
                                }}
                            />
                        </td>
                    </tr>
                    {record.expanded && record.loading ? (
                        <>
                            {this.renderStubs(record.depth + 2)}
                            {/* <CircularProgress size={24} color="primary" /> */}
                        </>
                    ) : (
                        <></>
                    )}
                    {record.children &&
                        record.children.length > 0 &&
                        record.expanded &&
                        this.renderTreeRecords(record.children, [...parents, term])}
                </React.Fragment>
            );
        });
    }

    renderStubs(count: number) {
        return (
            <>
                {Array(count)
                    .fill(0)
                    .map((x, i) => (
                        <span key={i} className="tree-stub" />
                    ))}
            </>
        );
    }

    renderStubsAndExpander(record: TreeRecord) {
        return (
            <>
                {this.renderStubs(record.depth)}
                {record.childCount > 0 ? (
                    <span
                        onClick={async () => await this.toggleRowExpand(record)}
                        className={cn("tree-stub row-expander", {
                            "is-expanded": record.expanded
                        })}
                    >
                        {/* TODO: Replace materal-icons md-16 by correct image class */}
                        <i className="oi oi-chevron-right" />{" "}
                    </span>
                ) : (
                    <span className="tree-stub" />
                )}
            </>
        );
    }

    async toggleRowExpand(record: TreeRecord) {
        if (record.expanded) {
            record.expanded = false;
        } else {
            try {
                if (record.childCount > 0 && record.children && record.children.length == 0) {
                    // Force it to expand before triggering the service
                    record.loading = true;
                    record.expanded = true;
                    this.forceUpdate();
                }

                await this.loader.expandTreeLevel(record);
            } catch (ex) {
                this.setState({ error: ex as Error });
            }
        }
        this.forceUpdate();
    }
}
